import React, { useMemo, useState } from "react";
import CsaQuestionCard from "./CsaQuestionCard";

/**
 * The assessment pane — pass one, done standing up (design §7e, §7f).
 *
 * Left rail: six pillars with live counts, used to jump. Main pane: one
 * sub-pillar at a time, questions grouped by element. One sub-pillar rather
 * than one long scroll because it is the unit the auditor actually walks, and
 * the unit Mark N/A and answer-by-exception act on.
 */
export default function CsaAssess({
  framework,
  auditId,
  photos,
  onPhotosChanged,
  answers,
  counts,
  summary,
  excludedSubs,
  activeSub,
  setActiveSub,
  readOnly,
  previous,
  onConfirm,
  onAnswer,
  onComment,
  assistEnabled,
  onBulkYes,
  onExclude,
  onRestoreScope,
}) {
  const [scopeTarget, setScopeTarget] = useState(null);

  /** Flat order of sub-pillars, so Previous and Next cross pillar boundaries. */
  const order = useMemo(() => {
    const out = [];
    framework?.pillars?.forEach((p) =>
      p?.sub_pillars?.forEach((s) => out?.push({ pillar: p, sub: s }))
    );
    return out;
  }, [framework]);

  const at = order.findIndex((o) => o.sub.name === activeSub);
  const current = at >= 0 ? order[at] : order[0];
  const exclusion = excludedSubs[current?.sub.name] || null;

  const subConformance = useMemo(() => {
    const m = {};
    if (!summary || !summary.pillars) return m;
    summary.pillars.forEach((p) =>
      (p.subPillars || []).forEach((s) => {
        m[s.name] = s.pct;
      })
    );
    return m;
  }, [summary]);

  const blanksHere = current?.sub?.elements?.reduce(
    (n, el) => n + el.questions.filter((q) => !(answers[q.code] || {}).answer).length,
    0
  );

  const subQuestionCount = current?.sub.elements?.reduce(
    (n, el) => n + el.questions.length,
    0
  );

  return (
    <div className="cap-body">
      {/* ---------------- left rail ---------------- */}
      {/*
        Pillars carry a progress bar, sub-pillars sit beneath them. Progress
        rather than conformance, because a bar that fills as you work answers
        "how far through am I" — which is the question an auditor actually has
        on site. The percentage is on the header, where it belongs.
      */}
      <nav className="cap-rail" aria-label="Pillars">
        <p className="rail-hd">Pillars</p>
        {framework?.pillars?.map((p) => {
          const c = counts?.byPillar[p?.pillar_no] || { done: 0, total: 0 };
          const pctDone = c?.total ? Math.round((c.done / c.total) * 100) : 0;
          const inThisPillar = p?.sub_pillars?.some((s) => s.name === current?.sub?.name);

          return (
            <div className={"rail-pillar" + (inThisPillar ? " act" : "")} key={p.pillar_no}>
              <div className="pname">
                <span>
                  P{p.pillar_no} {shorten(p.pillar_name)}
                </span>
                <span className="pmeta">
                  {c.done}/{c.total}
                </span>
              </div>
              <div className="rail-bar">
                <i style={{ width: pctDone + "%" }} />
              </div>

              {p?.sub_pillars?.map((s) => {
                const sc = counts.bySub[s.name] || { done: 0, total: 0 };
                const isExcluded = !!excludedSubs[s.name];
                // How many carried answers here nobody has looked at yet. A
                // fully-carried audit reads as 163/163 done in the rail, which
                // is true of the marking and false of the work.
                const openCarry = s?.elements?.reduce(
                  (n, el) =>
                    n +
                    el.questions.filter((q) => {
                      const a = answers[q?.code];
                      return a && a.carried_from_audit_id && !a.is_confirmed;
                    }).length,
                  0
                );
                const cls = [
                  "rail-sub",
                  s.name === current?.sub?.name ? "active" : "",
                  isExcluded ? "excluded" : "",
                  sc.done === sc.total && sc.total > 0 ? "done" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <button
                    type="button"
                    key={s?.name}
                    className={cls}
                    onClick={() => setActiveSub(s?.name)}
                  >
                    <span>{s?.name}</span>
                    <span className="cnt">
                      {isExcluded ? "N/A" : `${sc?.done}/${sc?.total}`}
                    </span>
                    {openCarry ? (
                      <span className="cnt-carry" title={openCarry + " carried, not yet checked"}>
                        {openCarry}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* ---------------- main pane ---------------- */}
      <main className="cap-main">
        <div className="sub-head">
          <div>
            <div className="crumb">
              P{current?.pillar?.pillar_no} · {current?.pillar?.pillar_name}
            </div>
            <h2>
              {current?.sub?.name}
              {subConformance[current?.sub?.name] == null ? null : (
                <span className="sub-pct">{subConformance[current?.sub?.name]}% conformance</span>
              )}
            </h2>
          </div>

          {/*
            Both N/A controls name their scope. They used to read "Not
            applicable" in both places — identical words, identical styling —
            with one excluding the whole sub-pillar and the other a single
            element. That is not a harmless mis-tap: an exclusion drops
            questions out of the score AND prints its justification in the
            client's report, so the wrong button tells a client you did not
            assess things you did.
          */}
          <div className="sub-actions">
            <button
              type="button"
              className="small-btn"
              disabled={readOnly || !!exclusion || blanksHere === 0}
              title="Sets only the questions you have not answered"
              onClick={() => onBulkYes(current?.sub?.name)}
            >
              Mark rest of sub-pillar Yes{blanksHere ? ` (${blanksHere})` : ""}
            </button>
            <button
              type="button"
              className="small-btn warn"
              disabled={readOnly || !!exclusion}
              onClick={() =>
                setScopeTarget({
                  level: "sub_pillar",
                  value: current?.sub?.name,
                  count: subQuestionCount,
                })
              }
            >
              Mark sub-pillar N/A
            </button>
          </div>
        </div>

        {exclusion ? (
          <div className="scope-banner">
            <div>
              <strong>Out of scope.</strong>{" "}
              <span className="why">{exclusion?.justification}</span>
            </div>
            <button
              type="button"
              className="small-btn"
              style={{ marginLeft: "auto" }}
              disabled={readOnly}
              onClick={() => onRestoreScope(exclusion?.id)}
            >
              Put back in scope
            </button>
          </div>
        ) : null}

        {current?.sub?.elements?.map((el) => (
          <section className="element" key={el.name}>
            <div className="element-head">
              <h3>{el?.name}</h3>
              {/* Was a bare number, which read like a numbering that had gone wrong. */}
              <span className="n">
                {el?.questions?.length} question{el?.questions?.length === 1 ? "" : "s"}
              </span>
              {excludedSubs[el.name] ? null : (
                <button
                  type="button"
                  className="small-btn warn"
                  style={{ marginLeft: "auto" }}
                  disabled={readOnly || !!exclusion}
                  onClick={() =>
                    setScopeTarget({
                      level: "element",
                      value: el.name,
                      count: el.questions.length,
                    })
                  }
                >
                  Mark element N/A
                </button>
              )}
            </div>

            {el.questions.map((q) => (
              <CsaQuestionCard
                key={q.code}
                question={q}
                auditId={auditId}
                photos={photos}
                onPhotosChanged={onPhotosChanged}
                answer={answers[q.code]}
                previous={previous ? previous[q.code] : null}
                onConfirm={onConfirm}
                disabled={readOnly}
                scopedOutBy={
                  (answers[q.code] || {}).na_source && exclusion
                    ? exclusion.justification
                    : null
                }
                onAnswer={onAnswer}
                onComment={onComment}
                assistEnabled={assistEnabled}
              />
            ))}
          </section>
        ))}
      </main>

      {/* ---------------- footer nav ---------------- */}
      <div className="cap-foot">
        <button
          type="button"
          className="small-btn"
          disabled={at <= 0}
          onClick={() => setActiveSub(order[at - 1].sub.name)}
        >
          ‹ Previous
        </button>
        <span className="where">
          {at + 1} of {order.length} sub-pillars
        </span>
        <button
          type="button"
          className="small-btn"
          disabled={at >= order.length - 1}
          onClick={() => setActiveSub(order[at + 1].sub.name)}
        >
          Next ›
        </button>
      </div>

      {scopeTarget ? (
        <ScopeDialog
          target={scopeTarget}
          onCancel={() => setScopeTarget(null)}
          onConfirm={(justification) => {
            onExclude(scopeTarget.level, scopeTarget.value, justification);
            setScopeTarget(null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The justification is not paperwork — it prints in the client's report against
 * every question it covers, so the client can see what was not assessed and
 * why. That is also why it cannot be skipped.
 */
function ScopeDialog({ target, onCancel, onConfirm }) {
  const [text, setText] = useState("");
  const ready = text.trim().length >= 10;

  const scope = target.level === "sub_pillar" ? "sub-pillar" : "element";

  return (
    <>
      <div className="csa-pad-scrim" onClick={onCancel} />
      <div className="scope-dialog" role="dialog" aria-modal="true">
        <h3>Mark this {scope} not applicable</h3>

        {/*
          Names the scope, the thing, and the number of questions before the
          auditor commits — so the two N/A buttons cannot be confused even if
          the wrong one was pressed.
        */}
        <p className="scope-what">
          <span className="scope-level">{scope}</span>
          <strong>{target.value}</strong>
          {target.count ? (
            <span className="scope-count">
              {target.count} question{target.count === 1 ? "" : "s"}
            </span>
          ) : null}
        </p>

        <p>
          Every unanswered question here becomes N/A and drops out of the score.
          Answers you have already given are kept as they are.
        </p>
        <label htmlFor="scope-why">Reason — this is printed in the report</label>
        <textarea
          id="scope-why"
          rows={3}
          value={text}
          placeholder="e.g. No owned or contracted vehicle fleet operates from this site."
          onChange={(e) => setText(e.target.value)}
        />
        <div className="scope-actions">
          <button type="button" className="small-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="cap-btn" disabled={!ready} onClick={() => onConfirm(text.trim())}>
            Mark not applicable
          </button>
        </div>
      </div>
    </>
  );
}

/** The rail is 260px; full pillar names wrap to three lines and push it out. */
function shorten(name) {
  const s = String(name || "");
  return s.length > 26 ? s.slice(0, 24).trimEnd() + "…" : s;
}
